import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Customer, CustomerCreated } from '@tabservice/tabfile-client';
import { MoreHorizontal, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/menu';
import { Field, Input, SearchInput } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/toggles';
import { EmptyState, ListSkeleton, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import {
  Table,
  TablePagination,
  TableToolbar,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { adminCustomersPageQuery, formsQuery, queryKeys } from '@/lib/queries';
import { modeCopy } from '@/lib/mode';
import { tabfile, errorMessage } from '@/lib/api';
import { formatDate } from '@/lib/format';

export const Route = createFileRoute('/_app/admin/')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <ListSkeleton rows={8} columns={5} />,
  loader: ({ context }) => context.queryClient.ensureQueryData(adminCustomersPageQuery('', 1, PAGE_SIZE)),
  component: AdminCustomersPage,
});

const PAGE_SIZE = 10;

function AdminCustomersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const forms = useQuery(formsQuery());

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  // No form is enabled until staff pick one: a pre-ticked 1099-NEC was being
  // saved unnoticed on customers who never file it (TS-8284).
  const [enabledForms, setEnabledForms] = useState<string[]>([]);
  const [created, setCreated] = useState<CustomerCreated | null>(null);
  /** The customer whose access is being suspended or restored, or null when none is. */
  const [statusTarget, setStatusTarget] = useState<Customer | null>(null);

  const createCustomer = useMutation({
    mutationFn: () =>
      tabfile.POST('/v1/admin/customers', {
        body: { name: name.trim(), ownerEmail: ownerEmail.trim(), enabledForms },
      }),
    onSuccess: (result) => {
      setCreated(result);
      setName('');
      setOwnerEmail('');
      setEnabledForms([]);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers });
    },
    onError: (error) => toast.error('Could not create the customer', errorMessage(error)),
  });

  /**
   * Suspending is how Tab Service cuts a customer off, and it is reachable from this list
   * because this list is where an administrator goes looking for it. It is the same call the
   * customer's own page makes: not a delete, and reversible from the same menu.
   */
  const setStatus = useMutation({
    mutationFn: (target: Customer) =>
      tabfile.PATCH('/v1/admin/customers/{customerId}', {
        params: { path: { customerId: target.id } },
        body: { status: target.status === 'active' ? 'suspended' : 'active' },
      }),
    onSuccess: (updated) => {
      toast.success(
        updated.status === 'suspended' ? `${updated.name} is suspended` : `${updated.name} is active again`,
        updated.status === 'suspended'
          ? 'Nobody on this account can sign in or file. E-files already sent to the IRS will still finish.'
          : 'Everyone on this account can sign in and file again.',
      );
      setStatusTarget(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomer(updated.id) });
    },
    onError: (error) => toast.error('Could not change the status', errorMessage(error)),
  });

  // The search goes to Tab Service after a short pause; pages come back newest first and
  // page numbers run over what has been fetched.
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(term.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [term]);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [search]);
  const customers = useQuery(adminCustomersPageQuery(search, page, PAGE_SIZE));
  const rows: Customer[] = customers.data?.items ?? [];
  const total = customers.data?.total ?? rows.length;

  /** Every New customer dialog starts empty — nothing carries over from the last one. */
  function openNewCustomer() {
    setCreated(null);
    setName('');
    setOwnerEmail('');
    setEnabledForms([]);
    setOpen(true);
  }

  function openCustomer(customerId: string) {
    void navigate({ to: '/admin/customers/$customerId', params: { customerId } });
  }

  return (
    <Card data-tour="admin-customers">
      <TableToolbar
        search={
          <SearchInput
            value={term}
            aria-label="Search customers"
            onChange={(event) => setTerm(event.target.value)}
          />
        }
      >
        <Button
          data-tour="admin-new-customer"
          onClick={openNewCustomer}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New customer
        </Button>
      </TableToolbar>

      {customers.isPending ? (
        <TableSkeleton rows={5} columns={6} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={term ? 'No customers match the search' : 'No customers yet'}
          description={term ? 'Clear the search to see them all.' : 'Create the first customer.'}
          action={
            term ? (
              <Button variant="outline" onClick={() => setTerm('')}>
                Clear the search
              </Button>
            ) : (
              <Button
                onClick={openNewCustomer}
              >
                New customer
              </Button>
            )
          }
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>
                  Customer
                </TH>
                <TH>
                  Mode
                </TH>
                <TH>
                  Status
                </TH>
                <TH>Forms</TH>
                <TH>
                  Created
                </TH>
                <TH>
                  <span className="sr-only">Actions</span>
                </TH>
              </tr>
            </THead>
            <TBody>
              {rows.map((customer) => (
                <TR
                  key={customer.id}
                  interactive
                  tabIndex={0}
                  role="link"
                  aria-label={`Open ${customer.name}`}
                  onClick={() => openCustomer(customer.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openCustomer(customer.id);
                    }
                  }}
                >
                  <TD className="font-medium">{customer.name}</TD>
                  <TD>
                    <Pill tone={modeCopy[customer.mode].tone} title={modeCopy[customer.mode].hint}>
                      {modeCopy[customer.mode].label}
                    </Pill>
                  </TD>
                  <TD>
                    <Pill tone={customer.status === 'active' ? 'success' : 'warning'}>
                      {customer.status === 'active' ? 'Active' : 'Suspended'}
                    </Pill>
                  </TD>
                  <TD className="max-w-[240px] truncate text-muted-foreground">{customer.enabledForms.join(', ')}</TD>
                  <TD className="whitespace-nowrap text-muted-foreground">{formatDate(customer.createdAt)}</TD>
                  <TD className="w-px">
                    {/* The row itself opens the customer, so the menu has to keep its own
                        clicks and keys to itself. */}
                    <div
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <Menu>
                        <MenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Actions for ${customer.name}`}
                          >
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </MenuTrigger>
                        <MenuContent>
                          <MenuItem onSelect={() => openCustomer(customer.id)}>
                            Open
                          </MenuItem>
                          <MenuItem
                            className="text-destructive data-[highlighted]:text-destructive data-[highlighted]:bg-[color:var(--ts-pill-danger-bg)]"
                            onSelect={() => setStatusTarget(customer)}
                          >
                            {customer.status === 'active'
                              ? 'Suspend'
                              : 'Reactivate'}
                          </MenuItem>
                        </MenuContent>
                      </Menu>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <TablePagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </TableWrap>
      )}

      <ConfirmDialog
        open={statusTarget !== null}
        onOpenChange={(next) => (next ? null : setStatusTarget(null))}
        title={
          statusTarget?.status === 'active'
            ? `Suspend ${statusTarget.name}`
            : `Reactivate ${statusTarget?.name ?? 'customer'}`
        }
        description={
          statusTarget?.status === 'active' ? (
            <>
              Nobody on this account will be able to sign in or file, and their API keys will be
              refused. Nothing is deleted: their users, keys and e-files stay as they are, and
              reactivating puts it all back.
              <br />
              <br />
              E-files already on their way to the IRS will finish, so their acknowledgements are
              still recorded.
            </>
          ) : (
            <>
              Everyone on this account will be able to sign in and file again, with the keys and
              settings they had before.
            </>
          )
        }
        confirmLabel={statusTarget?.status === 'active' ? 'Suspend' : 'Reactivate'}
        destructive={statusTarget?.status === 'active'}
        requireTyped={statusTarget?.status === 'active' ? 'SUSPEND' : undefined}
        pending={setStatus.isPending}
        onConfirm={() => {
          if (statusTarget) setStatus.mutate(statusTarget);
        }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          title={created ? `${created.name} is set up` : 'New customer'}
          description={
            created
              ? 'The owner has been invited.'
              : 'New customers start in IRS test mode. Nothing is filed for real until you switch them to live.'
          }
          footer={
            created ? (
              <DialogClose asChild>
                <Button variant="primary">Done</Button>
              </DialogClose>
            ) : (
              <>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  variant="primary"
                  disabled={
                    name.trim() === '' ||
                    !/.+@.+\..+/.test(ownerEmail.trim()) ||
                    enabledForms.length === 0 ||
                    createCustomer.isPending
                  }
                  onClick={() => createCustomer.mutate()}
                >
                  {createCustomer.isPending ? <Spinner /> : null}
                  Create customer
                </Button>
              </>
            )
          }
        >
          {created ? (
            <div className="flex flex-col gap-3 text-base text-gray-3">
              <p>
                <span className="font-medium">{created.owner.email}</span> has been invited as the owner
                {created.invitation.emailed ? ' and the invitation email is on its way.' : '.'}
              </p>
              {created.invitation.emailed ? null : (
                <p className="text-sm text-muted-foreground">
                  No mail service is connected, so the invitation was not emailed. The owner can use
                  Forgot password on the sign-in page to set a password.
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Once signed in, the owner creates their own API keys under Settings and invites
                colleagues. Tab Service staff never see a customer&apos;s keys.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Field label="Company name">
                {(props) => (
                  <Input
                    {...props}
                    value={name}
                    autoFocus
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Northwind Payroll"
                  />
                )}
              </Field>
              <Field label="Owner email" hint="They receive the invite and can add their own users.">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={ownerEmail}
                    onChange={(event) => setOwnerEmail(event.target.value)}
                    placeholder="owner@company.com"
                  />
                )}
              </Field>

              <fieldset className="flex flex-col gap-2">
                <legend className="mb-2 text-base font-medium text-gray-3">Forms they can file</legend>
                <div className="grid grid-cols-2 gap-2">
                  {(forms.data?.forms ?? []).map((form) => {
                    const checked = enabledForms.includes(form.formType);
                    return (
                      <label
                        key={form.formType}
                        className="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]"
                      >
                        <Checkbox
                          checked={checked}
                          label={form.formType}
                          onCheckedChange={(next) =>
                            setEnabledForms((current) =>
                              next ? [...current, form.formType] : current.filter((value) => value !== form.formType),
                            )
                          }
                        />
                        <span className="text-base text-gray-3">{form.formType}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
